import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Database,
  Fingerprint,
  LayoutGrid,
  MapPin,
  RotateCcw,
  Save,
  Shield,
  Trash2,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { useAuth, useRole } from "../../lib/auth/hooks";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import * as productsApi from "../../lib/api/products";
import type { Product, ProductModel, ProductStatus, DataCatalog } from "../../lib/api/products";
import * as productAccessApi from "../../lib/api/product-access";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { useI18n } from "../../lib/i18n";
import * as s from "./ProductPage.css";
import * as dangerStyles from "../users/UserAccountDangerZone.css";
import { OrgThumbnail } from "../../../ui/components/OrgThumbnail";
import { TransferProductOwnerModal } from "./TransferProductOwnerModal";

// ─── Form shape ───────────────────────────────────────────────────────────────

interface FormValues {
  // Overview
  name: string;
  productModelId: string;
  status: ProductStatus;
  source: string;
  isSimulated: boolean;
  pvCapacityW: string;
  // Identifiers
  assetTag: string;
  serial: string;
  macAddress: string;
  iotThingName: string;
  // Telemetry
  dataCatalogId: string;
  // Location
  address: string;
  addressSecondary: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  timezone: string;
  latitude: string;
  longitude: string;
}

function productToForm(p: Product): FormValues {
  return {
    name: p.name,
    productModelId: p.productModelId ?? "",
    status: p.status as ProductStatus,
    source: p.source,
    isSimulated: p.isSimulated,
    pvCapacityW: p.pvCapacityW ?? "",
    assetTag: p.assetTag ?? "",
    serial: p.serial ?? "",
    macAddress: p.macAddress ?? "",
    iotThingName: p.iotThingName ?? "",
    dataCatalogId: p.dataCatalogId ?? "",
    address: p.address ?? "",
    addressSecondary: p.addressSecondary ?? "",
    city: p.city ?? "",
    state: p.state ?? "",
    zip: p.zip ?? "",
    country: p.country ?? "",
    timezone: p.timezone ?? "",
    latitude: p.latitude ?? "",
    longitude: p.longitude ?? "",
  };
}

function emptyForm(): FormValues {
  return productToForm({} as Product);
}

function isFieldDirty(key: keyof FormValues, values: FormValues, saved: FormValues): boolean {
  return String(values[key] ?? "").trim() !== String(saved[key] ?? "").trim();
}

function sectionDirty(
  keys: (keyof FormValues)[],
  values: FormValues,
  saved: FormValues,
): boolean {
  return keys.some((k) => isFieldDirty(k, values, saved));
}

function dirtyFieldCount(
  keys: (keyof FormValues)[],
  values: FormValues,
  saved: FormValues,
): number {
  return keys.filter((k) => isFieldDirty(k, values, saved)).length;
}

const SMART_OWNED_FIELDS = [
  "productModelId",
  "isSimulated",
  "pvCapacityW",
  "serial",
  "macAddress",
  "iotThingName",
  "latitude",
  "longitude",
] as const satisfies readonly (keyof productsApi.UpdateProductPayload)[];

const OVERVIEW_KEYS: (keyof FormValues)[] = [
  "name",
  "productModelId",
  "status",
  "isSimulated",
  "pvCapacityW",
];
const IDENTIFIER_KEYS: (keyof FormValues)[] = [
  "assetTag",
  "serial",
  "macAddress",
  "iotThingName",
];
const TELEMETRY_KEYS: (keyof FormValues)[] = ["dataCatalogId"];
const LOCATION_KEYS: (keyof FormValues)[] = [
  "address",
  "addressSecondary",
  "city",
  "state",
  "zip",
  "country",
  "timezone",
  "latitude",
  "longitude",
];

const SECTION_IDS = {
  overview: "product-edit-overview",
  identifiers: "product-edit-identifiers",
  telemetry: "product-edit-telemetry",
  location: "product-edit-location",
  security: "product-edit-security",
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

export function ProductEditPage({ productId }: { productId: string }) {
  const { t, tx } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const { hasRole } = useRole("super-admin");
  const isSuperAdmin = hasRole();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(`product-edit-${productId}`);

  const [product, setProduct] = React.useState<Product | null>(null);
  const [productModels, setProductModels] = React.useState<ProductModel[]>([]);
  const [dataCatalogs, setDataCatalogs] = React.useState<DataCatalog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [values, setValues] = React.useState<FormValues>(emptyForm());
  const [savedValues, setSavedValues] = React.useState<FormValues>(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [dangerError, setDangerError] = React.useState<string | null>(null);
  const [dangerExpanded, setDangerExpanded] = React.useState(false);
  const [access, setAccess] = React.useState<productAccessApi.EffectiveProductAccess | null>(null);
  const [transferOwnerOpen, setTransferOwnerOpen] = React.useState(false);

  const { confirm, ConfirmModal } = useConfirmModal();

  React.useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      productsApi.getProductById(token, productId),
      productsApi.fetchProductModels(token),
      productsApi.fetchDataCatalogs(token),
      productAccessApi.fetchProductAccessCheck(token, productId),
    ])
      .then(([p, models, catalogs, accessCheck]) => {
        const tier = accessCheck.access?.tier;
        const canEdit = tier === "edit" || tier === "full";
        if (!accessCheck.allowed || !canEdit) {
          navigate({ to: "/workspace/products/$productId", params: { productId }, replace: true });
          return;
        }
        setProduct(p);
        setAccess(accessCheck.access ?? null);
        setProductModels(models);
        setDataCatalogs(catalogs.filter((c) => c.isActive));
        const form = productToForm(p);
        setValues(form);
        setSavedValues(form);
      })
      .catch((err) => setLoadError(err?.message || "Failed to load product"))
      .finally(() => setLoading(false));
  }, [token, productId, navigate]);

  const patch = React.useCallback((field: keyof FormValues, value: string | boolean) => {
    setSaveError(null);
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const overviewDirty = sectionDirty(OVERVIEW_KEYS, values, savedValues);
  const identifierDirty = sectionDirty(IDENTIFIER_KEYS, values, savedValues);
  const telemetryDirty = sectionDirty(TELEMETRY_KEYS, values, savedValues);
  const locationDirty = sectionDirty(LOCATION_KEYS, values, savedValues);

  const overviewChangeCount = dirtyFieldCount(OVERVIEW_KEYS, values, savedValues);
  const identifierChangeCount = dirtyFieldCount(IDENTIFIER_KEYS, values, savedValues);
  const telemetryChangeCount = dirtyFieldCount(TELEMETRY_KEYS, values, savedValues);
  const locationChangeCount = dirtyFieldCount(LOCATION_KEYS, values, savedValues);
  const totalDirtyCount =
    overviewChangeCount +
    identifierChangeCount +
    telemetryChangeCount +
    locationChangeCount;
  const hasDirty = overviewDirty || identifierDirty || telemetryDirty || locationDirty;
  const smartManaged = product?.smartLink?.linkEnabled === true;

  const handleResetAll = React.useCallback(() => {
    setValues(savedValues);
    setSaveError(null);
  }, [savedValues]);

  const handleSaveAll = React.useCallback(async () => {
    if (!token) {
      setSaveError("Session expired. Please sign in again.");
      return;
    }

    if (overviewDirty && !values.name.trim()) {
      setSaveError("Product name is required.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload: productsApi.UpdateProductPayload = {};

    if (overviewDirty) {
      payload.name = values.name.trim();
      payload.productModelId = values.productModelId || null;
      payload.status = values.status;
      payload.isSimulated = values.isSimulated;
      payload.pvCapacityW = values.pvCapacityW ? parseFloat(values.pvCapacityW) : null;
    }
    if (identifierDirty) {
      payload.assetTag = values.assetTag.trim() || null;
      payload.serial = values.serial.trim() || null;
      payload.macAddress = values.macAddress.trim() || null;
      payload.iotThingName = values.iotThingName.trim() || null;
    }
    if (telemetryDirty) {
      payload.dataCatalogId = values.dataCatalogId || null;
    }
    if (locationDirty) {
      payload.address = values.address || null;
      payload.addressSecondary = values.addressSecondary || null;
      payload.city = values.city || null;
      payload.state = values.state || null;
      payload.zip = values.zip || null;
      payload.country = values.country || null;
      payload.timezone = values.timezone || null;
      payload.latitude = values.latitude ? parseFloat(values.latitude) : null;
      payload.longitude = values.longitude ? parseFloat(values.longitude) : null;
    }

    if (smartManaged) {
      for (const field of SMART_OWNED_FIELDS) delete payload[field];
    }

    try {
      const updated = await productsApi.updateProduct(token, productId, payload);
      setProduct(updated);
      const form = productToForm(updated);
      setValues(form);
      setSavedValues(form);
      void queryClient.invalidateQueries({ queryKey: ["products", "list"] });
    } catch (e) {
      setSaveError(getApiErrorMessage(e, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  }, [
    token,
    productId,
    values,
    overviewDirty,
    identifierDirty,
    telemetryDirty,
    locationDirty,
    queryClient,
    smartManaged,
  ]);

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!hasDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: saving,
        onClick: handleResetAll,
      },
      {
        id: "save",
        label:
          totalDirtyCount === 1
            ? `Save ${totalDirtyCount} change`
            : `Save ${totalDirtyCount} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: saving,
        disabled: saving,
        onClick: () => void handleSaveAll(),
      },
    ];
  }, [hasDirty, totalDirtyCount, saving, handleResetAll, handleSaveAll, t]);

  React.useEffect(() => {
    if (!hasDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasDirty]);

  const handleBack = () =>
    navigate({ to: "/workspace/products/$productId", params: { productId } });

  if (loading) {
    return (
      <div className={s.page}>
        <div className={s.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>
            {tx("shared.common.loading")}
          </Text>
        </div>
      </div>
    );
  }

  if (loadError || !product) {
    return (
      <DetailPageLayout
        backLink={{ label: "Back to product", onClick: handleBack }}
        header={{
          media: <Box size={28} color="#9CA3AF" aria-hidden />,
          title: "Edit product",
        }}
      >
        <Callout variant="error">{loadError ?? "Product not found."}</Callout>
      </DetailPageLayout>
    );
  }

  // Resolve the effective default catalog by mapping productLine → slug, mirroring the backend.
  const selectedModel = productModels.find((m) => m.id === values.productModelId) ?? null;
  const productLineSlug = selectedModel
    ? selectedModel.productLine.startsWith("sanctuary")
      ? "sanctuary"
      : selectedModel.productLine === "cabinet"
        ? "cabinet"
        : selectedModel.productLine === "container"
          ? "container"
          : null
    : null;
  const modelDefaultCatalog = productLineSlug
    ? (dataCatalogs.find((c) => c.slug === productLineSlug) ?? null)
    : null;

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.overview,
      label: t("pro.products.overview"),
      icon: <LayoutGrid size={14} />,
    },
    {
      id: SECTION_IDS.identifiers,
      label: t("pro.products.identifiers"),
      icon: <Fingerprint size={14} />,
    },
    {
      id: SECTION_IDS.telemetry,
      label: t("pro.products.telemetry"),
      icon: <Database size={14} />,
    },
    {
      id: SECTION_IDS.location,
      label: t("pro.products.location"),
      icon: <MapPin size={14} />,
    },
    {
      id: SECTION_IDS.security,
      label: t("pro.users.security"),
      icon: <Shield size={14} />,
    },
  ]);

  return (
    <>
      <DetailPageLayout
        backLink={{
          label: "Back to product",
          onClick: handleBack,
        }}
        header={{
          media: <Box size={28} color="#9CA3AF" aria-hidden />,
          title: "Edit product",
          subtitle: values.name.trim() || product.name,
        }}
        {...sectionLayout}
        floatingActions={floatingActions}
      >
        {smartManaged && (
          <div className={s.calloutWrap}>
            <Callout variant="info">{t("pro.products.smartLink.managedNotice")}</Callout>
          </div>
        )}
        {saveError && (
          <div style={{ marginBottom: 16 }}>
            <Callout variant="error">{saveError}</Callout>
          </div>
        )}

        <CollapsibleSection
          id={SECTION_IDS.overview}
          title={t("pro.products.overview")}
          icon={<LayoutGrid size={20} />}
          expanded={isExpanded(SECTION_IDS.overview)}
          onToggleExpanded={() => toggle(SECTION_IDS.overview)}
          highlight={overviewDirty ? "accent" : false}
          statusIndicators={
            overviewDirty
              ? [<UnsavedChangesChip key="u" count={overviewChangeCount} />]
              : undefined
          }
        >
          <div className={s.cardInner}>
            <TextInput
              label={t("pro.products.productNameRequired")}
              value={values.name}
              onChange={(e) => patch("name", e.target.value)}
              disabled={saving}
              changed={values.name.trim() !== savedValues.name.trim()}
            />

            <div className={s.fieldGroup}>
              <span className={s.formFieldLabel}>{tx("pro.products.productModel")}</span>
              <select
                value={values.productModelId}
                onChange={(e) => patch("productModelId", e.target.value)}
                disabled={saving || smartManaged}
                className={clsx(
                  s.formSelect,
                  values.productModelId !== savedValues.productModelId && s.formSelectChanged,
                )}
              >
                <option value="">— None —</option>
                {productModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={s.fieldRow}>
              <div className={s.fieldGroup}>
                <span className={s.formFieldLabel}>{tx("pro.products.status")}</span>
                <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                  {(["active", "inactive", "decommissioned"] as ProductStatus[]).map((opt) => (
                    <label
                      key={opt}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={opt}
                        checked={values.status === opt}
                        onChange={() => patch("status", opt)}
                        disabled={saving}
                      />
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={values.isSimulated}
                onChange={(e) => patch("isSimulated", e.target.checked)}
                disabled={saving || smartManaged}
                style={{
                  width: 16,
                  height: 16,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color:
                      values.isSimulated !== savedValues.isSimulated
                        ? "var(--color-primary, #3B82F6)"
                        : "var(--color-fg)",
                  }}
                >
                  {tx("pro.products.simulatedProduct")}
                </span>
                <span style={{ fontSize: 12, color: "var(--color-fgSecondary)" }}>
                  {tx("pro.products.simulatedProductHint")}
                </span>
              </div>
            </label>

            <div style={{ maxWidth: "50%" }}>
              <TextInput
                label={t("pro.products.solarCapacityW")}
                value={values.pvCapacityW}
                onChange={(e) => patch("pvCapacityW", e.target.value)}
                disabled={saving || smartManaged}
                changed={values.pvCapacityW !== savedValues.pvCapacityW}
                placeholder="e.g. 10000"
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.identifiers}
          title={t("pro.products.identifiers")}
          icon={<Fingerprint size={20} />}
          expanded={isExpanded(SECTION_IDS.identifiers)}
          onToggleExpanded={() => toggle(SECTION_IDS.identifiers)}
          highlight={identifierDirty ? "accent" : false}
          statusIndicators={
            identifierDirty
              ? [<UnsavedChangesChip key="u" count={identifierChangeCount} />]
              : undefined
          }
        >
          <div className={s.cardInner}>
            <div className={s.fieldRow}>
              <TextInput
                label={t("pro.products.assetTag")}
                value={values.assetTag}
                onChange={(e) => patch("assetTag", e.target.value)}
                disabled={saving}
                changed={values.assetTag.trim() !== savedValues.assetTag.trim()}
                placeholder="e.g. LE-A1B2C3"
              />
              <TextInput
                label={t("pro.products.serialNumber")}
                value={values.serial}
                onChange={(e) => patch("serial", e.target.value)}
                disabled={saving || smartManaged}
                changed={values.serial.trim() !== savedValues.serial.trim()}
                placeholder="e.g. SN-000123"
              />
            </div>
            <TextInput
              label={t("pro.products.macAddress")}
              value={values.macAddress}
              onChange={(e) => patch("macAddress", e.target.value)}
              disabled={saving || smartManaged}
              changed={values.macAddress.trim() !== savedValues.macAddress.trim()}
              placeholder="e.g. AA:BB:CC:DD:EE:FF"
            />
            <TextInput
              label={t("pro.products.iotThingName")}
              value={values.iotThingName}
              onChange={(e) => patch("iotThingName", e.target.value)}
              disabled={saving || smartManaged}
              changed={values.iotThingName.trim() !== savedValues.iotThingName.trim()}
              placeholder={t("pro.products.placeholder.iotThingName")}
            />
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.telemetry}
          title={t("pro.products.telemetry")}
          icon={<Database size={20} />}
          expanded={isExpanded(SECTION_IDS.telemetry)}
          onToggleExpanded={() => toggle(SECTION_IDS.telemetry)}
          highlight={telemetryDirty ? "accent" : false}
          statusIndicators={
            telemetryDirty
              ? [<UnsavedChangesChip key="u" count={telemetryChangeCount} />]
              : undefined
          }
        >
          <div className={s.cardInner}>
            <div className={s.fieldGroup}>
              <span className={s.formFieldLabel}>{t("pro.system.dataCatalog.title")}</span>
              <select
                value={values.dataCatalogId}
                onChange={(e) => patch("dataCatalogId", e.target.value)}
                disabled={saving}
                className={clsx(
                  s.formSelect,
                  telemetryDirty &&
                    values.dataCatalogId !== savedValues.dataCatalogId &&
                    s.formSelectChanged,
                )}
              >
                <option value="">
                  {modelDefaultCatalog
                    ? `Default for model (${modelDefaultCatalog.name})`
                    : "— No catalog —"}
                </option>
                {dataCatalogs.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.id === modelDefaultCatalog?.id ? " (model default)" : ""}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: "var(--color-fgMuted, #9CA3AF)", marginTop: 2 }}>
                {values.dataCatalogId
                  ? "This catalog overrides the model default for this product."
                  : "Using the default catalog for this product model."}
              </span>
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.location}
          title={t("pro.products.location")}
          icon={<MapPin size={20} />}
          expanded={isExpanded(SECTION_IDS.location)}
          onToggleExpanded={() => toggle(SECTION_IDS.location)}
          highlight={locationDirty ? "accent" : false}
          statusIndicators={
            locationDirty
              ? [<UnsavedChangesChip key="u" count={locationChangeCount} />]
              : undefined
          }
        >
          <div className={s.cardInner}>
            <TextInput
              label={t("pro.products.address")}
              value={values.address}
              onChange={(e) => patch("address", e.target.value)}
              disabled={saving}
              changed={values.address !== savedValues.address}
            />
            <TextInput
              label={t("pro.products.address2")}
              value={values.addressSecondary}
              onChange={(e) => patch("addressSecondary", e.target.value)}
              disabled={saving}
              changed={values.addressSecondary !== savedValues.addressSecondary}
            />
            <div className={s.fieldRow}>
              <TextInput
                label={t("pro.products.city")}
                value={values.city}
                onChange={(e) => patch("city", e.target.value)}
                disabled={saving}
                changed={values.city !== savedValues.city}
              />
              <TextInput
                label={t("pro.products.state")}
                value={values.state}
                onChange={(e) => patch("state", e.target.value)}
                disabled={saving}
                changed={values.state !== savedValues.state}
              />
            </div>
            <div className={s.fieldRow}>
              <TextInput
                label={t("pro.products.zip")}
                value={values.zip}
                onChange={(e) => patch("zip", e.target.value)}
                disabled={saving}
                changed={values.zip !== savedValues.zip}
              />
              <TextInput
                label={t("pro.products.country")}
                value={values.country}
                onChange={(e) => patch("country", e.target.value)}
                disabled={saving}
                changed={values.country !== savedValues.country}
              />
            </div>
            <TextInput
              label={t("pro.products.timezone")}
              value={values.timezone}
              onChange={(e) => patch("timezone", e.target.value)}
              disabled={saving}
              changed={values.timezone !== savedValues.timezone}
              placeholder="e.g. America/Denver"
            />
            <div className={s.fieldRow}>
              <TextInput
                label={t("pro.products.latitude")}
                value={values.latitude}
                onChange={(e) => patch("latitude", e.target.value)}
                disabled={saving || smartManaged}
                changed={values.latitude !== savedValues.latitude}
                placeholder="e.g. 40.7128"
              />
              <TextInput
                label={t("pro.products.longitude")}
                value={values.longitude}
                onChange={(e) => patch("longitude", e.target.value)}
                disabled={saving || smartManaged}
                changed={values.longitude !== savedValues.longitude}
                placeholder="e.g. -74.0060"
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          id={SECTION_IDS.security}
          title={t("pro.users.security")}
          icon={<Shield size={20} />}
          expanded={isExpanded(SECTION_IDS.security)}
          onToggleExpanded={() => toggle(SECTION_IDS.security)}
        >
          <div className={s.cardInner}>
            {access?.canManage && (
              <div className={s.ownershipBlock}>
                <div>
                  <h3 className={s.ownershipTitle}>Ownership</h3>
                  {access.owningOrganization?.name && (
                    <div className={s.ownershipOwner}>
                      <OrgThumbnail
                        name={access.owningOrganization.name}
                        logoUrl={access.owningOrganization.logoUrl}
                        size={22}
                      />
                      {access.owningOrganization.name}
                    </div>
                  )}
                  <p className={s.ownershipDescription}>
                    Members of the owning organization get access based on their organization role.
                  </p>
                </div>
                <Button
                  type="button"
                  color="neutral"
                  variant="outline"
                  onClick={() => setTransferOwnerOpen(true)}
                >
                  Transfer ownership
                </Button>
              </div>
            )}
            <div className={dangerStyles.dangerZone}>
              <button
                type="button"
                className={dangerStyles.dangerZoneToggleBtn}
                onClick={() => setDangerExpanded((e) => !e)}
                aria-expanded={dangerExpanded}
              >
                Danger zone
                <svg
                  className={`${dangerStyles.dangerZoneCaret} ${dangerExpanded ? dangerStyles.dangerZoneCaretOpen : ""}`}
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {dangerExpanded && (
                <>
                  <div className={dangerStyles.dangerZoneActions}>
                    <Button
                      type="button"
                      color="danger"
                      variant="outline"
                      onClick={async () => {
                        if (!token) return;
                        const ok = await confirm(
                          "Decommission product?",
                          `"${product.name}" will be marked as decommissioned and removed from active views. You can restore it later by editing the status.`,
                          "Decommission",
                        );
                        if (!ok) return;
                        try {
                          setDangerError(null);
                          await productsApi.deleteProduct(token, productId);
                          navigate({ to: "/workspace/products" });
                        } catch (err: unknown) {
                          setDangerError(getApiErrorMessage(err, "Failed to decommission."));
                        }
                      }}
                    >
                      Decommission
                    </Button>
                    {isSuperAdmin && (
                      <Button
                        type="button"
                        color="danger"
                        variant="solid"
                        iconLeft={<Trash2 size={16} />}
                        onClick={async () => {
                          if (!token) return;
                          const ok = await confirm(
                            "Permanently delete product?",
                            `This will permanently remove "${product.name}" and all its components from the database. This cannot be undone.`,
                            "Delete permanently",
                          );
                          if (!ok) return;
                          try {
                            setDangerError(null);
                            await productsApi.hardDeleteProduct(token, productId);
                            navigate({ to: "/workspace/products" });
                          } catch (err: unknown) {
                            setDangerError(getApiErrorMessage(err, "Failed to delete."));
                          }
                        }}
                      >
                        Delete permanently
                      </Button>
                    )}
                  </div>
                  {dangerError && <Callout variant="error">{dangerError}</Callout>}
                </>
              )}
            </div>
          </div>
        </CollapsibleSection>
      </DetailPageLayout>

      <TransferProductOwnerModal
        open={transferOwnerOpen}
        setOpen={setTransferOwnerOpen}
        token={token}
        productId={productId}
        productName={product.name}
        currentOrganizationId={access?.owningOrganization?.id ?? null}
        onTransferred={() => {
          if (!token) return;
          void productAccessApi
            .fetchProductAccessCheck(token, productId)
            .then((check) => setAccess(check.access ?? null))
            .catch(() => {});
        }}
      />
      <ConfirmModal />
    </>
  );
}
